<template>
  <div class="auth-wrapper auth-v2">
    <b-row class="auth-inner m-0">
      <b-link class="brand-logo">
        <logo />
        <h2 class="brand-text text-primary ml-1">
          {{ appName }}
        </h2>
      </b-link>
      <b-navbar-nav class="nav align-items-center ml-auto absolute-locale">
        <locale />
      </b-navbar-nav>

      <b-col lg="8" class="d-none d-lg-flex align-items-center p-5">
        <div
          class="w-100 d-lg-flex align-items-center justify-content-center px-5"
        >
          <b-img fluid :src="imgUrl" alt="Login V2" />
        </div>
      </b-col>

      <b-col lg="4" class="d-flex align-items-center auth-bg px-2 p-lg-5">
        <b-col sm="8" md="6" lg="12" class="px-xl-2 mx-auto">
          <b-card-title title-tag="h2" class="font-weight-bold mb-1">
            {{ $t("breadcrumb.login") }}
          </b-card-title>

          <validation-observer ref="loginForm" #default="{ invalid }">
            <b-form class="auth-login-form mt-2" @submit.prevent="validateForm">
              <n-input v-model="data" :fields="fields">
                <template #password="item">
                  <b-form-group>
                    <div class="d-flex justify-content-between">
                      <label for="login-password">
                        {{ $t(item.field.label) }}
                      </label>
                      <b-link tabindex="-1" :to="{ name: 'forgot-password' }">
                        <small>{{ $t("breadcrumb.forgotPassword") }}</small>
                      </b-link>
                    </div>
                    <validation-provider
                      #default="{ errors }"
                      :vid="item.field.key"
                      :name="item.field.label"
                      :rules="item.field.rules"
                    >
                      <n-password-input
                        :id="item.field.key"
                        :ref="item.field.key"
                        v-model="data[item.field.key]"
                        :name="item.field.key"
                        :placeholder="
                          item.field.placeholder
                            ? $t(item.field.placeholder)
                            : $t(item.field.label)
                        "
                        :errors="errors"
                        :disabled="item.field.disabled"
                        :class="errors.length > 0 ? 'is-invalid' : null"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </template>
              </n-input>

              <n-button-loading
                type="submit"
                variant="primary"
                :loading="loading"
                :disabled="invalid"
                block
              >
                {{ $t("button.signIn") }}
              </n-button-loading>
            </b-form>
          </validation-observer>

          <b-card-text class="text-center mt-2">
            <span>{{ $t("general.newToPlatform") }} </span>
            <b-link class="link-underline" :to="{ name: 'register' }">
              <span>&nbsp;{{ $t("button.createAccount") }}</span>
            </b-link>
          </b-card-text>
        </b-col>
      </b-col>
    </b-row>
  </div>
</template>

<script>
/* eslint-disable global-require */
import Logo from "@core/layouts/components/Logo.vue";
import {
  BRow,
  BCol,
  BLink,
  BFormGroup,
  BCardText,
  BCardTitle,
  BImg,
  BForm,
  BButton,
  BNavbarNav,
} from "bootstrap-vue";
import useJwt from "@/auth/jwt/useJwt";
import { getHomeRouteForLoggedInUser } from "@/auth/utils";
import { initialAbility } from "@/libs/acl/config";
import store from "@/store/index";
import NPasswordInput from "@/components/NPasswordInput";
import NInput from "@/components/NInput";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { $themeConfig } from "@themeConfig";
import NButtonLoading from "@/components/NButtonLoading";
import Locale from "@/layouts/components/app-navbar/Locale.vue";
import { mapAbility } from "@/libs/helper";
import useCallStore from "@/store-pinia/useCallStore";
import FormInput from "./formInput";
import useAppStore from "@/store-pinia/useAppStore";
import serviceSidebar from "@/navigation/vertical/menu/service";

export default {
  components: {
    BRow,
    BCol,
    BLink,
    BFormGroup,
    BCardText,
    BCardTitle,
    BImg,
    BForm,
    BButton,
    BNavbarNav,
    Logo,
    Locale,
    NPasswordInput,
    NInput,
    NButtonLoading,
  },
  data() {
    return {
      sideImg: require("@/assets/images/pages/login-v2.svg"),
      // validation rulesimport store from '@/store/index'
      data: {
        email: "",
        password: "",
        status: "",
      },
      loading: false,
    };
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === "password" ? "EyeIcon" : "EyeOffIcon";
    },
    imgUrl() {
      if (store.state.appConfig.layout.skin === "dark") {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.sideImg = require("@/assets/images/pages/login-v2-dark.svg");
        return this.sideImg;
      }
      return this.sideImg;
    },
  },
  mounted() {
    this.serviceSidebar[0].tag = 0;
    this.appStore.resetServiceBadgeCount();
  },
  methods: {
    validateForm() {
      this.$refs.loginForm.validate().then((success) => {
        if (success) {
          this.submit();
        }
      });
    },
    submit() {
      this.loading = true;
      useJwt
        .login({
          ...this.data,
        })
        .then((response) => {
          const { data } = response.data || {};
          const userData = data.user;

          this.appStore.setProfile(userData);

          useJwt.setToken(data.accessToken);
          useJwt.setRefreshToken(data.refreshToken);
          localStorage.setItem(
            useJwt.jwtConfig.storageUserDataKeyName,
            JSON.stringify(userData)
          );
          this.$store.commit("profile/UPDATE_PROFILE", userData);

          let newAbility = [...initialAbility];
          if (userData.ability) {
            newAbility = mapAbility(userData, newAbility, initialAbility);
          }
          this.$ability.update(newAbility);
          this.$router
            .replace(
              getHomeRouteForLoggedInUser(
                "admin",
                this.$route.query.redirect,
                newAbility
              )
            )
            .then(() => {
              this.callStore.disposeAgent();
              this.callStore.initCallClient();

              this.$toast({
                component: ToastificationContent,
                position: "top-right",
                props: {
                  title: this.$t("general.welcomeUser", {
                    name: this.data.firstName,
                  }),
                  icon: "CoffeeIcon",
                  variant: "success",
                  text: this.$t("alert.successLoggedIn"),
                },
              });
            });
        })
        .catch((error) => {
          this.loading = false;
          if (error.response?.status === 422) {
            this.$refs.loginForm.setErrors(error.response.data.validate);
          }
        });
    },
  },
  setup() {
    const { appName } = $themeConfig.app;
    const fields = FormInput;
    const callStore = useCallStore();
    const appStore = useAppStore();

    return {
      appName,
      fields,
      callStore,
      appStore,
      serviceSidebar
    };
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/pages/page-auth.scss";
</style>
